import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { FavoriteButton } from "./FavoriteButton";

type EntityCardRow = {
  label: React.ReactNode;
  value: React.ReactNode;
};

type EntityCardProps = {
  title: React.ReactNode;
  badge?: React.ReactNode;
  /** Chaîne vide : pas de pastille (carte sans état de connexion). */
  statusDotClassName?: string;
  /** Texte de la pastille (info-bulle et lecteur d'écran). */
  statusTitle?: string;
  isFavorite?: boolean;
  onToggleFavorite?: (e: React.MouseEvent) => void;
  onView?: (e: React.MouseEvent) => void;
  /** Œil grisé (ex. box déconnectée), avec son explication dans viewTitle. */
  viewDisabled?: boolean;
  viewTitle?: string;
  rows: EntityCardRow[];
  /** Ouvre la fiche : toute la carte est cliquable, signalée par un chevron. */
  onClick?: () => void;
  className?: string;
};

// 44 px : taille minimale confortable au doigt. Les marges négatives gardent l'en-tête
// à sa hauteur visuelle malgré la zone de clic agrandie.
const TOUCH_TARGET =
  "h-11 w-11 -my-3 shrink-0 flex items-center justify-center";

const CARD = "w-full bg-white rounded-lg shadow border border-grey-light";

export const EntityCard = ({
  title,
  badge,
  statusDotClassName = "",
  statusTitle,
  isFavorite = false,
  onToggleFavorite,
  onView,
  viewDisabled = false,
  viewTitle = "",
  rows,
  onClick,
  className = "",
}: EntityCardProps) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Entrée sur l'étoile ou l'œil remonte jusqu'ici : seule la carte elle-même s'ouvre.
    if (!onClick || e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={`${CARD} overflow-hidden ${
        onClick
          ? "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-secondary"
          : ""
      } ${className}`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Le nom passe à la ligne plutôt que d'être coupé. Les cartes à actions (Produits,
          Comptes) mettent leur badge dans les lignes pour lui laisser la place. */}
      <div className="flex items-center gap-3 px-4 py-3 bg-grey-light">
        {statusDotClassName !== "" && (
          <span
            className={`h-4 w-4 shrink-0 rounded-full ${statusDotClassName}`}
            role={statusTitle ? "img" : undefined}
            aria-label={statusTitle}
            title={statusTitle}
          />
        )}
        {badge && <div className="shrink-0">{badge}</div>}
        <div className="flex-1 min-w-0 font-oxaniumMedium break-words">
          {title}
        </div>
        {onToggleFavorite && (
          <FavoriteButton
            isFavorite={isFavorite}
            onToggle={(e) => {
              e.stopPropagation();
              onToggleFavorite(e);
            }}
            className={`${TOUCH_TARGET} text-xl`}
          />
        )}
        {onView && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView(e);
            }}
            disabled={viewDisabled}
            title={viewTitle}
            aria-label={viewTitle}
            className={`${TOUCH_TARGET} text-xl ${
              viewDisabled
                ? "text-grey-medium cursor-not-allowed"
                : "text-tertiary hover:text-secondary transition-colors"
            }`}
          >
            <FontAwesomeIcon icon="eye" />
          </button>
        )}
        {onClick && (
          <FontAwesomeIcon
            icon="chevron-right"
            aria-hidden="true"
            className="shrink-0 text-grey-readable"
          />
        )}
      </div>

      <div>
        {rows.map((row, idx) => (
          <div
            key={idx}
            className={`flex items-center justify-between gap-4 px-4 py-3 ${idx === 0 ? "" : "border-t border-grey-light"}`}
          >
            <div className="shrink-0 text-sm text-grey-readable font-oxaniumMedium">
              {row.label}
            </div>
            {/* Sur mobile on ne peut pas survoler pour lire la suite : une valeur longue
                (IPv6, UUID, email) passe à la ligne plutôt que d'être tronquée. */}
            <div className="min-w-0 text-default-text text-right break-words">
              {row.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Cartes fantômes pendant le chargement, au rythme des lignes fantômes du tableau. */
export const EntityCardSkeletons = ({
  count = 4,
  rows = 3,
}: {
  count?: number;
  rows?: number;
}) => (
  <div aria-busy="true" className="space-y-4">
    {Array.from({ length: count }, (_, card) => (
      <div key={card} className={`${CARD} overflow-hidden`}>
        <div className="flex items-center gap-3 px-4 py-3 bg-grey-light">
          <div className="h-5 w-16 rounded-full bg-white animate-pulse" />
          <div className="h-4 flex-1 rounded bg-white animate-pulse" />
        </div>
        {Array.from({ length: rows }, (_, row) => (
          <div
            key={row}
            className={`flex justify-between gap-4 px-4 py-3 ${row === 0 ? "" : "border-t border-grey-light"}`}
          >
            <div className="h-4 w-20 rounded bg-grey-light animate-pulse" />
            <div className="h-4 w-32 rounded bg-grey-light animate-pulse" />
          </div>
        ))}
      </div>
    ))}
  </div>
);
